import { FC, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SplitButton } from 'primereact/splitbutton';
import { confirmDialog } from 'primereact/confirmdialog';
import { TratamentoSementes } from '../../@types';
import Agro1Loader from '../../components/Agro1Loader';
import { TratamentoSementesService } from '../../services';
import Agro1Toast from '../../components/Agro1Toast';
import usePrintTratamentoSementeStore from './store';

type ActionButtonsProps = {
  data: TratamentoSementes;
  listReloader: () => Promise<void>;
};

const ActionButtons: FC<ActionButtonsProps> = ({ data, listReloader }) => {
  const navigate = useNavigate();
  const fetchDataToPrint = usePrintTratamentoSementeStore(
    (state) => state.fetchDataToPrint,
  );

  const setPrintData = useCallback(async () => {
    const loaderId = Agro1Loader.show();
    try {
      const baseRecord = await TratamentoSementesService.find(String(data.id));
      await fetchDataToPrint(baseRecord);
    } catch (error) {
      console.error(error);

      Agro1Toast.show({
        detail: 'Erro ao carregar dados para impressão.',
        severity: 'error',
        sticky: true,
      });
    } finally {
      Agro1Loader.close(loaderId);
    }
  }, [data.id, fetchDataToPrint]);

  const deleteHandler = useCallback(async () => {
    const loaderId = Agro1Loader.show();

    try {
      await TratamentoSementesService.delete(data.id.toString());

      await listReloader();

      Agro1Toast.show({
        detail: 'Registro excluído com sucesso.',
        severity: 'success',
      });
    } catch (error) {
      console.error(error);
      Agro1Toast.show({
        detail: 'Falha ao excluir o registro.',
        severity: 'error',
        sticky: true,
      });
      throw new Error('Erro ao tentar excluir o registro.');
    } finally {
      Agro1Loader.close(loaderId);
    }
  }, [data.id, listReloader]);

  const beforeDeleteHandler = useCallback(() => {
    confirmDialog({
      message: 'Você tem certeza que deseja excluir?',
      header: 'Atenção',
      icon: 'pi pi-info-circle',
      accept: deleteHandler,
      acceptLabel: 'Excluir',
      rejectLabel: 'Cancelar',
      acceptClassName: 'button-danger',
      rejectClassName: 'button-primary',
    });
  }, [deleteHandler]);

  const items = useMemo(
    () => [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => navigate(`/tratamento-sementes/edit/${data.id}`),
      },
      {
        label: 'Imprimir',
        icon: 'pi pi-print',
        command: setPrintData,
      },
      {
        label: 'Excluir',
        icon: 'pi pi-times',
        command: beforeDeleteHandler,
      },
    ],
    [beforeDeleteHandler, data.id, navigate, setPrintData],
  );

  return (
    <SplitButton
      model={items}
      text
      dropdownIcon="pi pi-ellipsis-v"
      className="acoes"
      buttonClassName="p-0"
    />
  );
};

export default ActionButtons;
